import { useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  Outlet,
  redirect,
  isRedirect,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useApiClient, apiClient, refreshManager } from "@/lib/auth";
import { queryClient } from "@/lib/query-client";
import { tokenOrgId } from "@/lib/token-org";
import { clearAllDrafts } from "@/lib/test-entry/draft-store";
import { useAppStore } from "@/store";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { useDraftMaintenance } from "@/hooks/use-draft-maintenance";
import { ViewportBottomOwnerContext } from "@/hooks/use-own-viewport-bottom";
import { usePermissions } from "@/hooks/use-permissions";
import { Sidebar } from "@/components/ui/sidebar";
import { QuickActionsFab } from "@/components/quick-actions/quick-actions-fab";
import { RecentPagesPalette } from "@/components/recent-pages/recent-pages-palette";
import { clearBulkEditWatches } from "@/components/bulk-edits/bulk-edit-watch";
import { BulkEditRunWatcher } from "@/components/bulk-edits/bulk-edit-run-watcher";
import { useMyInvites, useOrgs } from "@/hooks/use-orgs";
import { useNoticeSweepIssues } from "@/hooks/use-notice-sweep-issues";
import { useAccessRequests } from "@/hooks/use-access-requests";
import { useChangeRequestCounts } from "@/hooks/use-change-requests";
import { useNeedsReviewCount } from "@/hooks/use-needs-review-count";
import { testActivityCountBadge } from "@/components/change-requests/change-request-count-badge";
import {
  MESSAGE_THREADS_PAGE_SIZE,
  unreadThreadCount,
  useMessageThreads,
} from "@/hooks/use-message-threads";
import { useAnalytics } from "@/hooks/use-analytics";
import { useSamsShortcut } from "@/hooks/use-sams-shortcut";
import { useRecentPagesRecorder } from "@/hooks/use-recent-pages-recorder";
import { useMfa } from "@/hooks/use-mfa";
import { MfaEnforcement } from "@/components/mfa-enforcement";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    // Fast path: no refresh token at all -> redirect immediately
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }

    // Proactive refresh: ensure a valid access token exists before rendering
    // 10-second threshold is an emergency gate (different from auth guard's 60s proactive buffer)
    const state = useAppStore.getState();
    if (!state.expiresAt || Date.now() >= state.expiresAt - 10_000) {
      try {
        const success = await refreshManager.refresh();
        if (!success) {
          throw redirect({
            to: "/login",
            search: { redirect: location.href },
          });
        }
      } catch (e) {
        if (isRedirect(e)) throw e;
        throw redirect({
          to: "/login",
          search: { redirect: location.href },
        });
      }
    }

    // Read after the refresh above: the token it minted is the one every
    // org-scoped request will carry, and a refresh drops the org claim when the
    // membership was removed or the org deactivated.
    const session = useAppStore.getState();
    if (tokenOrgId(session.token) !== session.activeOrgId) {
      forgetStaleOrg();
      throw redirect({
        to: "/select-org",
        search: { redirect: location.href },
      });
    }
  },
  component: AuthenticatedLayout,
});

/**
 * Drops an org context the token does not carry the way an org switch does:
 * the remembered org, and every cached read made under it.
 */
function forgetStaleOrg(): void {
  useAppStore.getState().clearActiveOrg();
  queryClient.removeQueries();
}

/**
 * The org-context gate in front of the shell. The API authorizes every
 * org-scoped request by the access token's org claim, so the shell renders only
 * while the remembered org is the one the token names. When they part — a
 * refresh that dropped a removed membership, a sibling tab's switch adopted
 * here, a token naming an org nothing remembers — no org-scoped read is issued:
 * the stale context is dropped and the picker opens with this page as the way
 * back. Nothing here picks an org for the member; the picker's switch re-mints
 * a consistent pair.
 */
function AuthenticatedLayout() {
  const token = useAppStore((s) => s.token);
  const activeOrgId = useAppStore((s) => s.activeOrgId);
  const router = useRouter();
  // The page the gate sent to the picker from, read off the router once, when
  // the gate arms — the layout does not re-render on every navigation to keep
  // it current. The shell stays down over it even once forgetting the org has
  // made the pair agree again (an org-less token beside no org): it must not
  // flash back in personal context while the picker loads.
  const [sentFrom, setSentFrom] = useState<string | null>(null);
  const diverged = tokenOrgId(token) !== activeOrgId;
  if (diverged && sentFrom === null) setSentFrom(router.latestLocation.href);

  useEffect(() => {
    if (sentFrom === null) return;
    forgetStaleOrg();
    void router.navigate({ to: "/select-org", search: { redirect: sentFrom } });
    // A navigation that resolves inside this layout instead of the picker —
    // Back before the picker arrived, even to the page it was sent from — ends
    // the hold, and the pair is judged afresh there.
    return router.subscribe("onResolved", () => setSentFrom(null));
  }, [sentFrom, router]);

  if (diverged || sentFrom !== null) return null;
  return <AuthenticatedShell />;
}

function AuthenticatedShell() {
  const { t } = useTranslation("auth");
  // A surface whose own sticky bottom bar must sit flush with the viewport —
  // the SAMS chat's composer, and any mounted submission composer, which
  // claims it through `useOwnViewportBottom` for exactly as long as it is
  // mounted — owns the viewport bottom: the launcher hides and the shell's
  // bottom reserve, which exists to clear that launcher, goes with it. A
  // sticky bottom bar pins to the scrollport's content edge, so under the
  // reserve it would float 6rem above the screen, and the launcher would
  // otherwise sit on the bar's Submit button. Anywhere else — the test-entry
  // picker included — the reserve keeps pagination tails out from under the
  // floating button, and the launcher stays reachable.
  const pathname = useLocation({ select: (location) => location.pathname });
  const [viewportBottomOwners, setViewportBottomOwners] = useState(0);
  const viewportBottomOwner = useMemo(
    () => ({
      claim: () => setViewportBottomOwners((n) => n + 1),
      release: () => setViewportBottomOwners((n) => n - 1),
    }),
    [],
  );
  const ownsViewportBottom = pathname.startsWith("/sams") || viewportBottomOwners > 0;
  const user = useAppStore((s) => s.user);
  const orgId = useAppStore((s) => s.activeOrgId);
  const api = useApiClient();
  // Nav gating reads the caller's RESOLVED effective permissions (union of
  // group grants → coarse role + per-entity read), NOT the stored
  // activeOrgRole. `isOrgAdmin` is coarse_role === "org_admin"; `can` gates
  // per-entity nav visibility. Both read false until the envelope resolves, so
  // gated items stay hidden (no flash of admin nav).
  const { isOrgAdmin, can } = usePermissions();
  // The SAMS chord (Cmd/Ctrl+Shift+K) is ask-grant-gated like the route it
  // opens — without the grant the chord is a no-op, not a bounce off a 403.
  useSamsShortcut(can("reports", "run"));
  useAuthGuard();
  useAnalytics();
  useRecentPagesRecorder();
  // Best-effort backstop for the logout-triggered draft clears (Sign Out
  // button + the forced-logout paths in `@/lib/auth`): sweeps any staged
  // offline test-entry draft older than the TTL on every authenticated-shell
  // mount, covering the case where none of those clears fired (tab closed
  // without signing out, a crash, or a session that outlives the TTL).
  useDraftMaintenance();
  const { data: invitesData } = useMyInvites();
  const pendingInviteCount = invitesData?.invites?.length ?? 0;
  // One-row read of the sweep's open worklist — the same cache entry the
  // Issues tab's badge uses — so the sidebar says how much the sweep needs.
  const { data: issuesData } = useNoticeSweepIssues({ limit: 1, offset: 0 });
  const noticeIssueCount = issuesData?.total_count ?? 0;
  // The SAME first page the Messages inbox reads, under the same key — the
  // badge and the page share one fetch. Off without the grant: the route 403s
  // and the nav item is hidden anyway.
  const { data: threadsData } = useMessageThreads(
    { limit: MESSAGE_THREADS_PAGE_SIZE, offset: 0 },
    { enabled: can("messages", "read") },
  );
  const unreadMessageCount = unreadThreadCount(threadsData?.items);
  // One-row read of the access-request inbox — the Testers entry's badge says
  // how many companies and testers are waiting on a decision. Off without the
  // grant: the Requests tab's verbs are gated anyway and the count would only
  // point at a surface the caller cannot act on.
  const { data: accessRequestsData } = useAccessRequests(
    { limit: 1, offset: 0 },
    { enabled: can("testers", "read") },
  );
  const accessRequestCount = accessRequestsData?.total_count ?? 0;
  // The counts read Test Activity's Change requests pill and the Out of
  // Compliance card share (one key, one request); off without
  // change_requests:read.
  const { data: changeRequestCounts } = useChangeRequestCounts();
  // The needs-review count the Submissions pill, the dashboard card and the
  // Test Activity badge share (one key, one request); off without
  // form_submissions:read.
  const { data: needsReviewCount } = useNeedsReviewCount();

  const resendMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/auth/resend-verification");
      if (error) throw error;
    },
  });

  // MFA enforcement: when org requires MFA and user has not set it up,
  // block access and show mandatory MFA setup screen.
  const { status: mfaStatus } = useMfa();
  const userMfaEnabled =
    mfaStatus.isSuccess && mfaStatus.data?.methods && mfaStatus.data.methods.length > 0;

  // Read require_mfa from the orgs API response for the active org.
  const { data: orgsData } = useOrgs();
  const orgRequiresMfa = useMemo(() => {
    if (!orgId || !orgsData?.orgs) return false;
    const org = orgsData.orgs.find((o) => o.id === orgId);
    return org?.require_mfa === true;
  }, [orgId, orgsData?.orgs]);

  // R8: Show loading spinner while MFA status is being fetched to prevent
  // a brief flash of app content before MFA enforcement kicks in.
  if (orgRequiresMfa && mfaStatus.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (orgRequiresMfa && mfaStatus.isSuccess && !userMfaEnabled) {
    return <MfaEnforcement />;
  }

  const handleSignOut = async () => {
    const { refreshToken } = useAppStore.getState();

    // Fire-and-forget: revoke refresh token on server
    if (refreshToken) {
      apiClient
        .POST("/auth/logout", {
          body: { refresh_token: refreshToken },
        })
        .catch(() => {
          // Silently ignore -- we are logging out regardless
        });
    }

    // Clear client state. The recent-pages clear is synchronous here for the
    // same reason it is in `forceLogout`: the hard navigation below outruns the
    // recorder's effect-based clear, stranding the trail in session storage.
    useAppStore.getState().clearActiveOrg();
    useAppStore.getState().clearAuth();
    useAppStore.getState().clearRecentPages();
    queryClient.clear();
    clearBulkEditWatches();

    // Best-effort: empty any staged offline draft (answers + evidence blobs)
    // at rest on logout. Drafts are already userId-scoped (see draftKey), so
    // a stale draft can never be RESTORED into a different user's session --
    // this clear is disk hygiene / defense-in-depth for local PII at rest,
    // not the boundary that prevents cross-user leakage. Must be awaited
    // BEFORE the synchronous navigate below, which tears down the page and
    // would otherwise abandon a fire-and-forget clear mid-flight.
    try {
      await clearAllDrafts();
    } catch {
      // Silently ignore -- we are logging out regardless
    }

    window.location.href = "/login";
  };

  return (
    <ViewportBottomOwnerContext.Provider value={viewportBottomOwner}>
      {/* As tall as the VISIBLE viewport (`h-dvh`): a phone's `100vh` is its
          height with the browser toolbars retracted, so a `h-screen` frame
          hides its own bottom edge under them while they show. Below `md` the
          frame is a column — the sidebar's static top bar, then `<main>` — and
          from `md` a row. */}
      <div className="flex h-dvh flex-col overflow-hidden bg-surface md:flex-row print:h-auto print:overflow-visible">
        {/* Before the sidebar so the mobile nav overlay, also z-30, covers it. */}
        {ownsViewportBottom ? null : <QuickActionsFab />}
        <RecentPagesPalette />
        {/* The one poller of applying bulk edit runs, whichever page is open. */}
        <BulkEditRunWatcher />
        <div className="contents print:hidden">
          <Sidebar
            onSignOut={handleSignOut}
            pendingInviteCount={pendingInviteCount}
            noticeIssueCount={noticeIssueCount}
            unreadMessageCount={unreadMessageCount}
            accessRequestCount={accessRequestCount}
            testActivityBadge={testActivityCountBadge(changeRequestCounts, needsReviewCount, t)}
            isOrgAdmin={isOrgAdmin}
            orgId={orgId}
            canReadEntity={can}
          />
        </div>
        {/* pb-24 reserves a safe area beneath every page that does not own the
          viewport bottom, clearing the quick-actions launcher's bottom-6 +
          size-11 footprint so a page's trailing control (typically
          `Pagination`'s Next button) never ends up underneath it — the launcher
          is fixed to the viewport, not to this scroll container, so nothing
          short of a standing reserve keeps it off content. print:pb-0 because
          the launcher is print:hidden. */}
        <main
          className={`flex-1 overflow-auto print:overflow-visible ${
            ownsViewportBottom ? "" : "pb-24 print:pb-0"
          }`}
        >
          {user && !user.emailVerified && (
            <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 print:hidden dark:border-amber-800 dark:bg-amber-950">
              <div className="flex items-center justify-between">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {t("verifyEmailBanner")}
                </p>
                <button
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending || resendMutation.isSuccess}
                  className="rounded-md bg-amber-600 px-3 py-1 text-sm text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {resendMutation.isSuccess
                    ? t("resendVerificationSuccess")
                    : resendMutation.isPending
                      ? "..."
                      : t("resendVerification")}
                </button>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </ViewportBottomOwnerContext.Provider>
  );
}
